import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import prepareAuthHeaders from "@utils/prepareAuthHeaders";
import type {
  GameSessionCreateRequest,
  GameSessionCreateResponse,
  GameRoundSubmitRequest,
  GameRoundSubmitResponse,
  GameSessionCompleteResponse,
  GameProgressResponse,
  GameSummaryResponse,
  GameLibraryResponse,
  GameDifficultyResponse,
} from "@models/game";

const BASE_API_URL = process.env.REACT_APP_API_URL;

export const gameApi = createApi({
  reducerPath: "gameApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${BASE_API_URL}/games`,
    prepareHeaders: prepareAuthHeaders,
  }),
  tagTypes: ["GameProgress", "GameSession", "GameLibrary", "GameDifficulty"],
  endpoints: (builder) => ({
    createSession: builder.mutation<
      GameSessionCreateResponse,
      GameSessionCreateRequest
    >({
      invalidatesTags: ["GameSession"],
      query: (body) => ({
        url: "sessions",
        method: "POST",
        body,
      }),
    }),
    submitRound: builder.mutation<
      GameRoundSubmitResponse,
      { sessionId: string; body: GameRoundSubmitRequest }
    >({
      query: ({ sessionId, body }) => ({
        url: `sessions/${sessionId}/rounds`,
        method: "POST",
        body,
      }),
    }),
    completeSession: builder.mutation<
      GameSessionCompleteResponse,
      { sessionId: string }
    >({
      invalidatesTags: ["GameProgress", "GameSession"],
      query: ({ sessionId }) => ({
        url: `sessions/${sessionId}`,
        method: "PATCH",
      }),
    }),
    getProgress: builder.query<GameProgressResponse, { patientId: string }>({
      providesTags: ["GameProgress"],
      query: ({ patientId }) => ({
        url: "progress",
        method: "GET",
        params: { patientId },
      }),
    }),
    getSummary: builder.query<
      GameSummaryResponse,
      { patientId: string }
    >({
      providesTags: ["GameProgress"],
      query: ({ patientId }) => ({
        url: "summary",
        method: "GET",
        params: { patientId },
      }),
    }),
    getLibrary: builder.query<
      GameLibraryResponse,
      { patientId: string }
    >({
      providesTags: ["GameLibrary"],
      query: ({ patientId }) => ({
        url: "",
        method: "GET",
        params: { patientId },
      }),
    }),
    getDifficulty: builder.query<
      GameDifficultyResponse,
      { gameId: string; patientId: string }
    >({
      providesTags: (_result, _err, arg) => [
        { type: "GameDifficulty", id: arg.gameId },
      ],
      query: ({ gameId, patientId }) => ({
        url: `${encodeURIComponent(gameId)}/difficulty`,
        method: "GET",
        params: { patientId },
      }),
    }),
    getContent: builder.query<
      Record<string, unknown>,
      { gameId: string; seed: string }
    >({
      query: ({ gameId, seed }) => ({
        url: `${encodeURIComponent(gameId)}/content/${encodeURIComponent(seed)}`,
        method: "GET",
      }),
    }),
    /** @deprecated Use createSession with { sessionType: "guided", patientId } */
    startSession: builder.mutation<
      GameSessionCreateResponse,
      { patientId: string }
    >({
      invalidatesTags: ["GameSession"],
      query: ({ patientId }) => ({
        url: "sessions",
        method: "POST",
        body: { sessionType: "guided" as const, patientId },
      }),
    }),
  }),
});

export const {
  useCreateSessionMutation,
  useSubmitRoundMutation,
  useCompleteSessionMutation,
  useGetProgressQuery,
  useGetSummaryQuery,
  useGetLibraryQuery,
  useGetDifficultyQuery,
  useGetContentQuery,
  useStartSessionMutation,
} = gameApi;
